import React from 'react';
import Select from 'react-select';
import { failureModeCreateSchema } from '../../../../../common/src/domain/survey/failureMode/failureModeSchema';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { metaDataDefinition, metaDataSchema } from '../../../utils/data/fmecaDataDefinition';
import {
    ramsAOptions,
    ramsCOptions,
    ramsEcOptions,
    ramsEnvOptions,
    ramsPOptions,
    ramsROptions,
    ramsSOptions,
    getTotalPriorityValue,
    getWeightedPriorityValue,
} from '../../../utils/data/rams';
import { reactSelectDefaultStyles } from '../../../utils/reactSelectDefaultStyles';
import { EditPanel, EditPanelButton, EditPanelButtons, EditPanelCell, EditPanelHeader, EditPanelRow } from '../../elements';
import { Component } from '../../utils/Component';
import {
    ValidatingEditPanelSaveButton,
    ValidatingFeedback,
    ValidatingFilterDropdownWithUrl,
    ValidatingForm,
    ValidatingFormContext,
    ValidatingFormDependentSection,
    ValidatingFormInputText,
    ValidatingFormTextArea,
} from '../../validatingElements';
import { RamssheepDropdownFmeca } from './RamsDropdownFMECA';
import { TotalRiskScore } from './TotalRiskScore';

interface IAnalysisFailureModeAddEditProps extends ITranslateProps {
    objectId: string;
    surveyId: string;
    elementId?: undefined | string;
    unitId?: undefined | string;
    manifestationId?: undefined | string;
    canRemove?: undefined | boolean;
    onCancel: () => void;
    onSaved: (id: string) => Promise<void>;
    failureModeId?: undefined | string;
    onRemove: (id: string) => Promise<void>;
    step: 'analysis' | 'verification';
}

class AnalysisFailureModeAddEditComponent extends Component<IAnalysisFailureModeAddEditProps, Record<string, unknown>> {
    public override render() {
        return (
            <EditPanel>
                <ValidatingForm
                    schema={this.getSchema()}
                    createUrl={`/api/surveys/${this.props.surveyId}/failureModes/`}
                    createUrlQueryParameters={{
                        elementId: this.props.elementId,
                        unitId: this.props.unitId,
                        manifestationId: this.props.manifestationId,
                    }}
                    onSubmitted={async (id) => this.props.onSaved(id)}
                    itemUrl={`/api/surveys/${this.props.surveyId}/failureModes/${this.props.failureModeId}`}
                    id={this.props.failureModeId}
                    onChange={async (key, _value, item, onChange) => this.handleItemChanged(key, item, onChange)}
                    defaultItem={{ metaData: {} }}
                >
                    <EditPanelRow>
                        <EditPanelHeader text={this.props.t('failureMode.functionAndFailureMode')} />
                    </EditPanelRow>

                    <EditPanelRow>
                        <div className="col-md-12">
                            <ValidatingFilterDropdownWithUrl
                                name="defaultFailureModeId"
                                url="/api/defaultFailureModes"
                                urlQueryParameters={{
                                    unitId: this.props.unitId,
                                    failureModeId: this.props.failureModeId,
                                }}
                                notSelectedText={
                                    this.props.failureModeId ? undefined : this.props.t('common.selectItem', this.props.t('failureMode.failureModes'))
                                }
                                otherSelectedText={`${this.props.t('common.other')}...`}
                            />
                        </div>
                    </EditPanelRow>
                    <EditPanelRow>
                        <div className="col-md-12">
                            <ValidatingFormDependentSection showIf={(item) => item.defaultFailureModeId === null}>
                                <ValidatingFormInputText name="customName" icon="fa fa-chain-broken" placeholder={this.props.t('common.name')} />
                            </ValidatingFormDependentSection>
                        </div>
                    </EditPanelRow>

                    <EditPanelRow>
                        <EditPanelHeader text="Data" />
                    </EditPanelRow>
                    <EditPanelRow>
                        <ValidatingFormContext.Consumer>
                            {(validatingForm) => (
                                <>
                                    {metaDataDefinition.map((metaDataField) => (
                                        <React.Fragment key={metaDataField.name}>
                                            {(metaDataField.showWhen === undefined || metaDataField.showWhen(validatingForm.item.metaData)) && (
                                                <EditPanelRow>
                                                    <label htmlFor="clientCompany" className="field-label col-md-3">
                                                        {metaDataField.name.includes('anders') ? '' : metaDataField.name}
                                                    </label>
                                                    <EditPanelCell rowCount={9}>
                                                        <label className="field">
                                                            {metaDataField.control === 'dropdown' && (
                                                                <Select
                                                                    isClearable={true}
                                                                    placeholder={metaDataField.name}
                                                                    value={this.mapMetadataFieldOptions(metaDataField.options).find(
                                                                        (i) =>
                                                                            i.value ===
                                                                            (validatingForm.item.metaData
                                                                                ? validatingForm.item.metaData[metaDataField.name] || ''
                                                                                : ''),
                                                                    )}
                                                                    options={this.mapMetadataFieldOptions(metaDataField.options)}
                                                                    onChange={async (option: any) => {
                                                                        const value = option ? (option.value ? option.value : null) : null;
                                                                        await validatingForm.onChange('metaData', {
                                                                            ...validatingForm.item.metaData,
                                                                            [metaDataField.name]: value,
                                                                        });
                                                                    }}
                                                                    styles={reactSelectDefaultStyles}
                                                                />
                                                            )}
                                                            {metaDataField.control === 'textarea' && (
                                                                <textarea
                                                                    placeholder={metaDataField.name}
                                                                    value={
                                                                        validatingForm.item.metaData
                                                                            ? validatingForm.item.metaData[metaDataField.name] || ''
                                                                            : ''
                                                                    }
                                                                    onChange={(e) => {
                                                                        void validatingForm.onChange('metaData', {
                                                                            ...validatingForm.item.metaData,
                                                                            [metaDataField.name]: e.target.value,
                                                                        });
                                                                    }}
                                                                />
                                                            )}
                                                            {metaDataField.control === 'inputtext' && (
                                                                <input
                                                                    type="text"
                                                                    className="gui-input"
                                                                    placeholder={metaDataField.name}
                                                                    value={
                                                                        validatingForm.item.metaData
                                                                            ? validatingForm.item.metaData[metaDataField.name] || ''
                                                                            : ''
                                                                    }
                                                                    onChange={(e) => {
                                                                        void validatingForm.onChange('metaData', {
                                                                            ...validatingForm.item.metaData,
                                                                            [metaDataField.name]: e.target.value,
                                                                        });
                                                                    }}
                                                                />
                                                            )}
                                                            <ValidatingFeedback error={validatingForm.errors.get(`metaData.${metaDataField.name}`)} />
                                                        </label>
                                                    </EditPanelCell>
                                                </EditPanelRow>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </>
                            )}
                        </ValidatingFormContext.Consumer>
                    </EditPanelRow>
                    <EditPanelRow>
                        <div className="col-md-8">
                            <RamssheepDropdownFmeca
                                name="analysisRamsR"
                                topLabel="R"
                                header="Betrouwbaarheid"
                                subText="Manifestatie van risico"
                                options={ramsROptions}
                            />
                            <RamssheepDropdownFmeca
                                name="analysisRamsA"
                                topLabel="A"
                                header="Beschikbaarheid"
                                subText="Fractie van tijd dat object niet functioneert"
                                options={ramsAOptions}
                            />
                            <RamssheepDropdownFmeca
                                name="analysisRamsS"
                                topLabel="S"
                                header="Veiligheid"
                                subText="ARBO- en verkeersveiligheid"
                                options={ramsSOptions}
                            />
                            <RamssheepDropdownFmeca
                                name="analysisRamsC"
                                topLabel="C"
                                header="Verzorgingsniveau"
                                subText="Beeldkwalitieit van het object"
                                options={ramsCOptions}
                            />
                            <RamssheepDropdownFmeca
                                name="analysisRamsEc"
                                topLabel="E"
                                header="Gevolgkosten"
                                subText="De totale corectieve kosten"
                                options={ramsEcOptions}
                            />
                            <RamssheepDropdownFmeca
                                name="analysisRamsEnv"
                                topLabel="E"
                                header="Milieu"
                                subText="Negatief effect op leefomgevin"
                                options={ramsEnvOptions}
                            />
                            <RamssheepDropdownFmeca
                                name="analysisRamsP"
                                topLabel="P"
                                header="Imago"
                                subText="Media en politieke aandacht"
                                options={ramsPOptions}
                            />
                        </div>
                        <div className="col-md-4 mt35">
                            <TotalRiskScore step={this.props.step} />
                        </div>
                    </EditPanelRow>

                    {/* <EditPanelRow>
                        <EditPanelHeader text={this.props.t('common.remarks')} />
                    </EditPanelRow> */}
                    <EditPanelRow>
                        <div className="col-md-12">
                            <ValidatingFormTextArea name="analysisRemarks" placeholder={this.props.t('common.remarks')} />
                        </div>
                    </EditPanelRow>

                    {/* {this.props.step === 'analysis' && <>
                        <EditPanelRow>
                            <EditPanelHeader text={this.props.t('failureMode.analysisVerificationQuestion')} />
                        </EditPanelRow>
                        <EditPanelRow>
                            <div className="col-md-12">
                                <FormTextArea name="analysisVerificationQuestion" placeholder={this.props.t('failureMode.analysisVerificationQuestion')} />
                            </div>
                        </EditPanelRow>
                    </>} */}
                    <EditPanelButtons>
                        {this.props.failureModeId && this.props.canRemove && (
                            <EditPanelButton
                                onClick={async () => this.props.onRemove(this.props.failureModeId!)}
                                text={this.props.t('common.remove')}
                                small={true}
                            />
                        )}
                        <EditPanelButton onClick={() => this.props.onCancel()} text={this.props.t('common.cancel')} small={true} />
                        <ValidatingEditPanelSaveButton small={true} />
                    </EditPanelButtons>
                </ValidatingForm>
            </EditPanel>
        );
    }
    private mapMetadataFieldOptions(options?: { value: string; text: string }[]) {
        if (options === undefined) {
            return [];
        }
        return options.map((option) => ({ value: option.value, label: option.text }));
    }
    private async handleItemChanged(key: string, item: any, onChange: (key: any, value: any) => Promise<void>) {
        const keysToWatch = [
            'analysisRamsR',
            'analysisRamsA',
            'analysisRamsS',
            'analysisRamsC',
            'analysisRamsEc',
            'analysisRamsEnv',
            'analysisRamsP',
        ];

        if (keysToWatch.includes(key)) {
            const analysisRamsTotalPriority = getTotalPriorityValue(
                item.analysisRamsR,
                item.analysisRamsA,
                item.analysisRamsS,
                item.analysisRamsC,
                item.analysisRamsEc,
                item.analysisRamsEnv,
                item.analysisRamsP,
            );
            const analysisRamsWeightedPriority = getWeightedPriorityValue(
                item.analysisRamsR,
                item.analysisRamsA,
                item.analysisRamsS,
                item.analysisRamsC,
                item.analysisRamsEc,
                item.analysisRamsEnv,
                item.analysisRamsP,
            );
            await onChange('analysisRamsTotalPriority', analysisRamsTotalPriority.toString());
            await onChange('analysisRamsWeightedPriority', analysisRamsWeightedPriority);
        }
    }

    private getSchema() {
        return failureModeCreateSchema.keys({
            metaData: metaDataSchema,
        });
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const AnalysisFailureModeAddEdit = translate()(AnalysisFailureModeAddEditComponent);
